import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { enqueueTask } from 'ember-concurrency';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { fn, hash } from '@ember/helper';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import type { TOC } from '@ember/component/template-only';
import {
  PhArrowsClockwise,
  PhCloudArrowUp,
  PhDotsThreeVertical,
  PhFile,
  PhFileCsv,
  PhFileDoc,
  PhFileImage,
  PhFileJpg,
  PhFilePdf,
  PhFilePng,
  PhFilePpt,
  PhFileText,
  PhFileTxt,
  PhFileXls,
  PhFileZip,
  PhInfo,
  PhTrash,
} from 'ember-phosphor-icons';
import Button from 'tio-reimagined/components/button';
import Chip from 'tio-reimagined/components/chip';
import ReSelect from 'tio-reimagined/components/select';
import { Popover } from 'tio-ui/components/popover';
import Table from 'tio-ui/components/table';
import { eq } from 'tio-ui/utilities';
import safeFormatDate from '../../../../helpers/safe-format-date.ts';
import FileUploader from '../../../file-uploader.gts';
import { DrawerHeader, DrawerBody } from 'tio-reimagined/components/drawer';
import type TasApplicationModel from '../../../../models/tas-application.ts';
import type TasAssetModel from '../../../../models/tas-asset.ts';
import type AssetSummaryModel from '../../../../models/asset-summary.ts';
import type TasProgramTemplateModel from '../../../../models/tas-program-template.ts';
import type DigitalAssetsService from '../../../../services/digital-assets.ts';
import type StoreService from '@ember-data/store';

/**
 * Classification option for attachments
 */
export interface ClassificationOption {
  label: string;
  key: string;
}

/**
 * Upload Section Component - Yields the upload UI
 */
interface UploadSectionSignature {
  Args: {
    canUpload: boolean;
    showUploadSection: boolean;
    toggleUploadSection: () => void;
    selectedClassification: string;
    classificationDidChange: (key: string) => void;
    uploadFile: { perform: (file: unknown) => Promise<void>; isRunning: boolean };
    classificationOptions?: ClassificationOption[];
  };
}

const UploadSection: TOC<UploadSectionSignature> = <template>
  {{! Upload Button - Always visible at top }}
  {{#if @canUpload}}
    {{#unless @showUploadSection}}
      <Button
        @intent="primary"
        @appearance="outlined"
        @icon={{PhCloudArrowUp}}
        @label={{t
          "common.tas.approval.drawer.attachments.upload_attachment"
          default="Upload Attachment"
        }}
        class="w-full"
        {{on "click" @toggleUploadSection}}
        data-test-upload-attachment-button
      />
    {{/unless}}
  {{/if}}

  {{! Upload Section - Conditional }}
  {{#if @showUploadSection}}
    <div class="border border-blue-200 dark:border-blue-800 rounded-lg bg-white shadow-sm">
      {{! Header }}
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div class="flex items-center gap-2">
          <PhCloudArrowUp @size="20" class="text-blue-600" />
          <h3 class="text-base font-semibold text-gray-900">
            {{t "tuition_assistance.program_details.upload_attachments"}}
          </h3>
        </div>
        <Button
          @appearance="minimal"
          @size="sm"
          @intent="default"
          {{on "click" @toggleUploadSection}}
          data-test-cancel-upload-button
        >
          {{t "common.cancel"}}
        </Button>
      </div>

      {{! Body }}
      <div class="px-6 py-5 space-y-5">
        {{! Classification Selection }}
        {{#if @classificationOptions}}
          <div>
            <label class="text-sm font-medium text-gray-700 block mb-2">
              {{t
                "tuition_assistance.program_details.courses.document_type"
                default="Document Type"
              }}
            </label>
            <ReSelect
              @items={{@classificationOptions}}
              @selected={{@selectedClassification}}
              {{! @glint-expect-error: onChange typed as string|null but handler only accepts string }}
              @onChange={{@classificationDidChange}}
              @clearable={{false}}
            />
          </div>
        {{/if}}

        {{! File Uploader }}
        <div>
          <FileUploader @didUploadFile={{@uploadFile.perform}} as |uploader|>
            <input
              id={{uploader.uniqueId}}
              type="file"
              multiple="true"
              {{uploader.queue.selectFile}}
              class="hidden"
              disabled={{@uploadFile.isRunning}}
            />
            <label
              for={{uploader.uniqueId}}
              class="group block cursor-pointer rounded-lg border-2 border-dashed transition-all
                {{if
                  @uploadFile.isRunning
                  'border-gray-300 bg-gray-50 cursor-not-allowed'
                  'border-gray-300 hover:border-blue-400 bg-gray-50 hover:bg-blue-50 dark:hover:border-blue-500'
                }}"
            >
              <div class="px-6 py-8 text-center">
                {{#if @uploadFile.isRunning}}
                  <PhArrowsClockwise @size="32" class="mx-auto mb-3 text-blue-600 animate-spin" />
                  <p class="text-sm font-medium text-gray-600">
                    Uploading...
                  </p>
                {{else}}
                  <PhCloudArrowUp
                    @size="32"
                    class="mx-auto mb-3 text-gray-400 dark:text-gray-600 group-hover:text-blue-600 transition-colors"
                  />
                  <div class="flex items-center justify-center gap-2 mb-2">
                    <span class="text-sm font-semibold text-blue-600 group-hover:text-blue-700">
                      {{t "choose_file" default="Choose File"}}
                    </span>
                    <span class="text-sm text-gray-500">
                      or drag and drop
                    </span>
                  </div>
                  <p class="text-xs text-gray-500">
                    You can upload multiple files
                  </p>
                {{/if}}
              </div>
            </label>
          </FileUploader>
        </div>
      </div>
    </div>
  {{/if}}
</template>;

/**
 * Table View Component - Yields the table view of attachments
 */
interface TableViewSignature {
  Args: {
    sortedAttachments: TasAssetModel[];
    getFileIcon: (extension: string) => typeof PhFile;
    getClassificationLabel: (classification: string) => string;
    isDeleted: (asset: TasAssetModel) => boolean;
    deleteAttachment: { perform: (asset: TasAssetModel) => Promise<void>; isRunning: boolean };
    disabled?: boolean;
    showUploadSection: boolean;
  };
}

const TableView: TOC<TableViewSignature> = <template>
  {{#if @sortedAttachments.length}}
    <div class="overflow-x-auto">
      <Table data-test-attachments-table>
        <:thead as |options|>
          <options.tr>
            <options.th class="w-full max-w-xs">
              {{t "common.tas.approval.drawer.attachments.file_name" default="File Name"}}
            </options.th>
            <options.th>
              {{t "common.tas.approval.drawer.attachments.type" default="Type"}}
            </options.th>
            <options.th>
              {{t "common.tas.approval.drawer.attachments.date" default="Date"}}
            </options.th>
            <options.th />
          </options.tr>
        </:thead>
        <:tbody as |options|>
          {{#each @sortedAttachments as |asset|}}
            <options.tr data-test-attachment-row>
              {{! File Name Column }}
              <options.td class="w-full max-w-xs">
                <div
                  class="flex items-center gap-1.5 overflow-hidden
                    {{if
                      (@isDeleted asset)
                      'line-through text-gray-400 dark:text-gray-600'
                      'text-tio-gray-700'
                    }}"
                >
                  {{#let (@getFileIcon asset.extension) as |IconComponent|}}
                    <IconComponent @size="20" class="flex-shrink-0" />
                  {{/let}}
                  <a
                    href={{asset.signedUrl}}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="font-semibold text-sm break-words min-w-0 hover:underline truncate"
                    data-test-attachment-filename
                  >
                    {{asset.filename}}
                  </a>
                </div>
              </options.td>

              {{! Type Badge Column }}
              <options.td>
                {{#let (@getClassificationLabel asset.classification) as |label|}}
                  {{#if label}}
                    <Chip
                      @size="sm"
                      @intent={{if
                        (eq asset.classification "CARD_USAGE_PROOF")
                        "warning"
                        "default"
                      }}
                      data-test-attachment-type-badge
                    >{{label}}</Chip>
                  {{/if}}
                {{/let}}
              </options.td>

              {{! Date Column }}
              <options.td>
                <span class="text-sm text-tio-gray-700" data-test-attachment-date>
                  {{safeFormatDate asset.createdAt month="2-digit" day="2-digit" year="2-digit"}}
                </span>
              </options.td>

              {{! Actions Column }}
              <options.td>
                {{! Always show actions menu }}
                <Popover @placement="bottom-end" as |pop|>
                  <button
                    type="button"
                    class="p-1 text-gray-600 hover:text-gray-900"
                    {{pop.trigger}}
                    {{pop.anchor}}
                    data-test-attachment-actions-trigger
                  >
                    <PhDotsThreeVertical @size="24" />
                  </button>
                  <pop.Content class="py-1 min-w-[160px]">
                    {{! View Info Action }}
                    <Popover @placement="left-start" as |infoPop|>
                      <button
                        type="button"
                        class="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        {{infoPop.trigger}}
                        {{infoPop.anchor}}
                        data-test-view-info-action
                      >
                        <PhInfo @size="16" class="flex-shrink-0" />
                        {{t "common.tas.approval.drawer.attachments.view_info" default="View Info"}}
                      </button>
                      <infoPop.Content class="p-4 text-sm max-w-xs">
                        <div class="space-y-2">
                          <div>
                            <div class="font-semibold text-gray-900">
                              {{t
                                "common.tas.approval.drawer.attachments.file_name"
                                default="File Name"
                              }}
                            </div>
                            <div class="text-gray-600 break-words">
                              {{asset.filename}}
                            </div>
                          </div>
                          <div>
                            <div class="font-semibold text-gray-900">
                              {{t
                                "common.tas.approval.drawer.attachments.file_type"
                                default="File Type"
                              }}
                            </div>
                            <div class="text-gray-600">
                              {{@getClassificationLabel asset.classification}}
                            </div>
                          </div>
                          <div>
                            <div class="font-semibold text-gray-900">
                              {{t
                                "common.tas.approval.drawer.attachments.upload_date"
                                default="Upload Date"
                              }}
                            </div>
                            <div class="text-gray-600">
                              {{safeFormatDate
                                asset.createdAt
                                month="2-digit"
                                day="2-digit"
                                year="numeric"
                              }}
                            </div>
                          </div>
                          {{#if (@isDeleted asset)}}
                            <div>
                              <div class="font-semibold text-gray-900">
                                {{t
                                  "common.tas.approval.drawer.attachments.date_deleted"
                                  default="Date Deleted"
                                }}
                              </div>
                              <div class="text-gray-600">
                                {{#if asset.attachmentDeletedAt}}
                                  {{safeFormatDate
                                    asset.attachmentDeletedAt
                                    month="2-digit"
                                    day="2-digit"
                                    year="numeric"
                                  }}
                                {{/if}}
                              </div>
                            </div>
                          {{/if}}
                          {{#if asset.size}}
                            <div>
                              <div class="font-semibold text-gray-900">
                                {{t "common.size" default="Size"}}
                              </div>
                              <div class="text-gray-600">
                                {{asset.size}}
                                bytes
                              </div>
                            </div>
                          {{/if}}
                        </div>
                      </infoPop.Content>
                    </Popover>

                    {{! Delete Action - Only show if not deleted }}
                    {{#unless (@isDeleted asset)}}
                      {{#unless @disabled}}
                        <button
                          type="button"
                          class="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                          {{on "click" (fn @deleteAttachment.perform asset)}}
                          disabled={{@deleteAttachment.isRunning}}
                          data-test-delete-action
                        >
                          <PhTrash @size="16" />
                          {{#if @deleteAttachment.isRunning}}
                            {{t "common.deleting" default="Deleting..."}}
                          {{else}}
                            {{t "common.delete" default="Delete"}}
                          {{/if}}
                        </button>
                      {{/unless}}
                    {{/unless}}
                  </pop.Content>
                </Popover>
              </options.td>
            </options.tr>
          {{/each}}
        </:tbody>
      </Table>
    </div>
  {{else}}
    {{! Empty State }}
    {{#unless @showUploadSection}}
      <div class="text-center py-12">
        <p class="text-sm text-gray-500">
          {{t
            "tuition_assistance.program_details.no_attachments"
            default="No attachments yet. Click 'Upload Attachment' to add files."
          }}
        </p>
      </div>
    {{/unless}}
  {{/if}}
</template>;

/**
 * Redesigned Attachments drawer component
 * Simplified UI with upload toggle and actions menu
 * Now yields sections to allow app-specific rendering
 */
export interface AttachmentsDrawerRedesignSignature {
  Args: {
    model: TasApplicationModel;
    allAttachments: TasAssetModel[];
    classificationOptions?: ClassificationOption[];
    defaultClassification?: string;
    programTemplate?: TasProgramTemplateModel;
    onUploadComplete?: () => void;
    onAttachmentDelete?: (asset: TasAssetModel) => void;
    disabled?: boolean;
  };
  Blocks: {
    default: [
      {
        UploadSection: typeof UploadSection;
        uploadProps: {
          canUpload: boolean;
          showUploadSection: boolean;
          toggleUploadSection: () => void;
          selectedClassification: string;
          classificationDidChange: (key: string) => void;
          uploadFile: { perform: (file: unknown) => Promise<void>; isRunning: boolean };
          classificationOptions?: ClassificationOption[];
        };
        TableView: typeof TableView;
        tableProps: {
          sortedAttachments: TasAssetModel[];
          getFileIcon: (extension: string) => typeof PhFile;
          getClassificationLabel: (key: string) => string;
          isDeleted: (asset: TasAssetModel) => boolean;
          deleteAttachment: {
            perform: (asset: TasAssetModel) => Promise<void>;
            isRunning: boolean;
          };
          disabled?: boolean;
          showUploadSection: boolean;
        };
        attachments: TasAssetModel[];
        assetSummaries: AssetSummaryModel[];
        deleteAttachment: {
          perform: (asset: TasAssetModel) => Promise<void>;
          isRunning: boolean;
        };
        isDeleted: (asset: TasAssetModel) => boolean;
      },
    ];
  };
}

export default class AttachmentsDrawerRedesign extends Component<AttachmentsDrawerRedesignSignature> {
  @service declare store: StoreService;
  @service declare digitalAssets: DigitalAssetsService;
  @service declare intl: IntlService;

  @tracked showUploadSection = false;
  @tracked selectedClassification =
    this.args.defaultClassification || 'APPLICATION_LIFECYCLE_DOCUMENTS';

  // File extension to icon mapping
  getFileIcon = (extension: string) => {
    const ext = extension.toLowerCase();
    switch (ext) {
      case 'pdf':
        return PhFilePdf;
      case 'doc':
      case 'docx':
        return PhFileDoc;
      case 'xls':
      case 'xlsx':
        return PhFileXls;
      case 'ppt':
      case 'pptx':
        return PhFilePpt;
      case 'txt':
        return PhFileTxt;
      case 'zip':
        return PhFileZip;
      case 'jpg':
      case 'jpeg':
        return PhFileJpg;
      case 'png':
        return PhFilePng;
      case 'img':
        return PhFileImage;
      case 'csv':
        return PhFileCsv;
      case 'text':
        return PhFileText;
      default:
        return PhFile;
    }
  };

  // Classification label mapping
  getClassificationLabel = (classification: string): string => {
    const prefix = 'common.tas.approval.drawer.attachments';
    const classificationMap: Record<string, string> = {
      COMPLETION_DOCUMENTS: this.intl.t(`${prefix}.final_approval_document`),
      COURSE_APPROVAL_DOCUMENTS: this.intl.t(`${prefix}.approval_document`),
      PROGRAM_APPROVAL_DOCUMENTS: this.intl.t(`${prefix}.program_approval_document`),
      APPLICATION_LIFECYCLE_DOCUMENTS: this.intl.t(`${prefix}.approval_document_admin`),
      PROGRAM_COMPLETION_DOCUMENTS: this.intl.t(`${prefix}.program_completion_document`),
      CARD_USAGE_PROOF: this.intl.t(`${prefix}.card_usage_receipt`),
    };

    return classificationMap[classification] || '';
  };

  // Sorted attachments (non-deleted first, newest first; then deleted)
  get sortedAttachments(): TasAssetModel[] {
    const assets = this.args.allAttachments ?? [];

    const notDeleted = assets
      .filter((a) => a.attachmentDeletedAt === null)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    const deleted = assets.filter((a) => a.attachmentDeletedAt !== null);

    return [...notDeleted, ...deleted];
  }

  // Asset summaries for AI analysis
  get assetSummaries() {
    const summaries = this.args.allAttachments
      .map((asset) => asset.assetSummary)
      .filter((summary) => summary !== null);
    return summaries;
  }

  get canUpload(): boolean {
    return !this.args.disabled && !!this.args.programTemplate;
  }

  // Toggle upload section visibility
  toggleUploadSection = () => {
    this.showUploadSection = !this.showUploadSection;
  };

  // Handle classification change
  classificationDidChange = (key: string) => {
    this.selectedClassification = key;
  };

  // Upload file handler
  uploadFile = enqueueTask(async (file) => {
    const asset = this.digitalAssets.createAssetFromS3Details('tasAsset', file);

    asset.classification = this.selectedClassification;

    const attachable = this.args.model;

    // Ensure parent records are saved
    if (attachable.isNew) {
      if (attachable.tasProgramInstance?.isNew) {
        await attachable.tasProgramInstance.save();
      }
      await attachable.save();
    }

    // @ts-expect-error - Dynamic relationship access
    asset.tasApplications.push(attachable);
    // @ts-expect-error - Dynamic relationship access
    attachable.tasAssets.push(asset);

    await asset.save();
    await attachable.save();

    // Close upload section and trigger callback
    this.showUploadSection = false;
    this.args.onUploadComplete?.();
  });

  // Delete attachment handler
  deleteAttachment = enqueueTask(async (asset: TasAssetModel) => {
    const attachment = asset.attachments[0];
    if (!attachment) {
      console.warn('No attachment found for asset', asset);
      return;
    }

    await attachment.destroyRecord();
    await asset.reload();

    this.args.onAttachmentDelete?.(asset);
  });

  // Download attachment

  // Check if attachment is deleted
  isDeleted = (asset: TasAssetModel): boolean => {
    return asset.attachmentDeletedAt !== null;
  };

  <template>
    <DrawerHeader>
      <h2 class="text-lg font-semibold text-gray-800">
        {{t "common.tas.approval.drawer.attachments.title" default="Attachments"}}
      </h2>
    </DrawerHeader>

    <DrawerBody>
      <div class="flex flex-col gap-6 p-6">
        {{! Yield to allow app-specific rendering }}
        {{yield
          (hash
            UploadSection=UploadSection
            uploadProps=(hash
              canUpload=this.canUpload
              showUploadSection=this.showUploadSection
              toggleUploadSection=this.toggleUploadSection
              selectedClassification=this.selectedClassification
              classificationDidChange=this.classificationDidChange
              uploadFile=this.uploadFile
              classificationOptions=@classificationOptions
            )
            TableView=TableView
            tableProps=(hash
              sortedAttachments=this.sortedAttachments
              getFileIcon=this.getFileIcon
              getClassificationLabel=this.getClassificationLabel
              isDeleted=this.isDeleted
              deleteAttachment=this.deleteAttachment
              disabled=@disabled
              showUploadSection=this.showUploadSection
            )
            attachments=this.sortedAttachments
            assetSummaries=this.assetSummaries
            deleteAttachment=this.deleteAttachment
            isDeleted=this.isDeleted
          )
        }}
      </div>
    </DrawerBody>
  </template>
}
